<template>
  <div class="musubi-page musubi-page--enrich-settings">
    <header class="musubi-page__head">
      <h1>Enrichment settings</h1>
      <p class="text-muted">
        Turn enrichment methods on/off (the chain runs the enabled ones; disabled
        methods can still be run selectively from a lead), and set the API keys
        the paid/keyed providers need.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
    <p v-if="message" class="alert alert-success py-2 small">{{ message }}</p>

    <MusubiSectionCard title="Methods" icon="layer-group">
      <p class="text-muted small">Mode: <strong>On</strong> always runs · <strong>Off</strong> only on-demand · <strong>Gap-fill</strong> (AI methods) runs only when its target field is still empty after the free + Companies House passes — a cheap safety net.</p>
      <table class="es-table">
        <thead><tr><th>Method</th><th class="es-c">Mode</th><th class="es-c">Backfill</th></tr></thead>
        <tbody>
          <tr v-for="m in methods" :key="m.name">
            <td>{{ m.label }} <span class="text-muted small">({{ m.name }})</span></td>
            <td class="es-c">
              <select v-model="m.mode" class="form-select form-select-sm es-mode">
                <option value="off">Off</option>
                <option value="on">On</option>
                <option v-if="m.supports_fallback" value="fallback">Gap-fill</option>
              </select>
            </td>
            <td class="es-c">
              <button class="btn btn-sm btn-link p-0" :disabled="runningAll === m.name" @click="runAll(m)">
                <CygnusIcon name="spinner" :spin="true" v-if="runningAll === m.name" />
                <span v-else>Run on all</span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="form-text small">Backfill queues this method (with override) across existing leads — runs in the background on the cron.</div>
      <hr>
      <label class="small d-block">
        <input type="checkbox" v-model="allowManualAi" :disabled="!aiConfigured">
        Allow a manual <strong>AI pass</strong> per prospect (the "AI enrich" button forces the AI methods even when they're off)
      </label>
      <div v-if="!aiConfigured" class="form-text small text-muted">No AI provider configured — set one under Settings ▸ AI engine to enable manual AI passes.</div>
      <hr>
      <label class="small d-block mb-1">
        <input type="checkbox" v-model="refresh.enabled">
        <strong>Periodic re-enrichment</strong> — keep enriched prospects fresh on a schedule (changes are audited)
      </label>
      <div class="es-refresh small text-muted" :class="{ 'opacity-50': !refresh.enabled }">
        Gap-fill every
        <input type="number" min="1" class="form-control form-control-sm d-inline-block" style="width:70px" v-model.number="refresh.gapfill_days" :disabled="!refresh.enabled"> days (fills gaps, no overwrite);
        full re-check every
        <input type="number" min="1" class="form-control form-control-sm d-inline-block" style="width:70px" v-model.number="refresh.full_days" :disabled="!refresh.enabled"> days (re-checks everything).
      </div>
    </MusubiSectionCard>

    <MusubiSectionCard title="Provider keys" icon="lock" class="es-keys">
      <div class="es-field">
        <label class="form-label small text-muted">Companies House API key
          <span v-if="keys.companies_house_api_key && keys.companies_house_api_key.set" class="es-set">set · {{ keys.companies_house_api_key.hint }}</span>
        </label>
        <input v-model="chKey" type="password" class="form-control form-control-sm"
          :placeholder="keys.companies_house_api_key && keys.companies_house_api_key.set ? 'Enter a new key to replace' : 'Paste your free Companies House API key'">
        <div class="form-text small">
          Free key from <a href="https://developer.company-information.service.gov.uk/" target="_blank" rel="noopener">developer.company-information.service.gov.uk</a>.
          Leave blank to keep the current key; clear + save to remove.
        </div>
      </div>

      <div class="es-field es-keys">
        <label class="form-label small text-muted">Google Places API key
          <span v-if="keys.google_places_api_key && keys.google_places_api_key.set" class="es-set">set · {{ keys.google_places_api_key.hint }}</span>
        </label>
        <input v-model="gplacesKey" type="password" class="form-control form-control-sm"
          :placeholder="keys.google_places_api_key && keys.google_places_api_key.set ? 'Enter a new key to replace' : 'Paste your Google Places API key'">
        <div class="form-text small">
          Sanctioned Google source for local business discovery + opening hours (keyed/billed). Leave blank to keep the current key; clear + save to remove.
        </div>
      </div>
    </MusubiSectionCard>

    <MusubiSectionCard title="Outreach brief" icon="note-sticky" class="es-keys">
      <p class="text-muted small">
        Tell the AI who you are + your outreach goal; it steers the generated opener for every lead
        (e.g. "We're a web design agency specialising in Shopify rebuilds — offer a free site audit, warm and concise").
      </p>
      <div class="es-field">
        <textarea v-model="outreachContext" rows="4" class="form-control form-control-sm"
          placeholder="Your business + outreach angle…"></textarea>
      </div>
      <div class="es-suggest">
        <input v-model="goal" class="form-control form-control-sm" placeholder="…or describe your goal and let AI draft the brief">
        <button class="btn btn-outline-primary btn-sm" :disabled="suggesting || !goal.trim()" @click="suggest">
          <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="suggesting" />Suggest with AI
        </button>
      </div>
    </MusubiSectionCard>

    <div class="es-actions">
      <button class="btn btn-primary btn-sm" :disabled="busy" @click="save">
        <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy" />Save settings
      </button>
    </div>
  </div>
</template>

<script>
/**
 * AdminEnrichmentSettings — global enrichment config: per-method on/off toggles
 * + provider API keys (masked). Talks to /api/v1/admin/leads/enrich-settings.
 */
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: 'AdminEnrichmentSettings',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return { api: useApi(), methods: [], keys: {}, chKey: '', gplacesKey: '', clearCh: false, outreachContext: '', goal: '', suggesting: false, busy: false, runningAll: null, allowManualAi: true, aiConfigured: false, refresh: { enabled: true, gapfill_days: 30, full_days: 90 }, error: null, message: '' };
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/leads/enrich-settings');
        const p = res?.data ?? res;
        this.methods = p.methods || [];
        this.keys = p.keys || {};
        this.outreachContext = p.outreach_context || '';
        this.allowManualAi = p.allow_manual_ai ?? true;
        this.aiConfigured = !!p.ai_configured;
        if (p.refresh) this.refresh = p.refresh;
      } catch (e) { this.error = e?.message || 'Failed to load settings.'; }
    },
    async suggest() {
      this.suggesting = true; this.error = null;
      try {
        const res = await this.api.post('/api/v1/admin/leads/outreach-context/suggest', { goal: this.goal.trim() });
        const p = res?.data ?? res;
        if (p.suggestion) { this.outreachContext = p.suggestion; this.message = 'Brief drafted — review + Save.'; }
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Suggestion failed.'; }
      this.suggesting = false;
    },
    async save() {
      this.busy = true; this.error = null; this.message = '';
      const body = { methods: {} };
      this.methods.forEach((m) => { body.methods[m.name] = m.mode; });
      if (this.chKey.trim() !== '') body.companies_house_api_key = this.chKey.trim();
      if (this.gplacesKey.trim() !== '') body.google_places_api_key = this.gplacesKey.trim();
      body.outreach_context = this.outreachContext;
      body.allow_manual_ai = this.allowManualAi;
      body.refresh = this.refresh;
      try {
        const res = await this.api.post('/api/v1/admin/leads/enrich-settings', body);
        const p = res?.data ?? res;
        this.methods = p.methods || this.methods;
        this.keys = p.keys || this.keys;
        this.outreachContext = p.outreach_context ?? this.outreachContext;
        if (p.allow_manual_ai !== undefined) this.allowManualAi = p.allow_manual_ai;
        this.chKey = '';
        this.gplacesKey = '';
        this.message = 'Settings saved.';
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Save failed.'; }
      this.busy = false;
    },
    async runAll(m) {
      if (!window.confirm(`Queue "${m.label}" for all existing leads? It runs in the background.`)) return;
      this.runningAll = m.name; this.error = null; this.message = '';
      try {
        const res = await this.api.post(`/api/v1/admin/leads/enrich-methods/${m.name}/run-all`, {});
        const p = res?.data ?? res;
        this.message = `Queued ${m.label} for ${p.enqueued} lead(s) — running in the background.`;
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Backfill failed.'; }
      this.runningAll = null;
    },
  },
};
</script>

<style scoped>
.musubi-page--enrich-settings { padding: var(--m-space-6, 32px); }
.es-table { width: 100%; border-collapse: collapse; max-width: 520px; }
.es-table th { text-align: left; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); font-weight: 600; padding: 8px 6px; border-bottom: 1px solid var(--m-border); }
.es-table td { padding: 9px 6px; border-bottom: 1px solid var(--m-border); }
.es-c { text-align: center; width: 90px; }
.es-toggle { background: none; border: none; cursor: pointer; font-size: 1.4rem; color: var(--m-text-tertiary); line-height: 1; }
.es-toggle.on { color: var(--m-accent); }
.es-mode { width: auto; display: inline-block; }
.es-keys { margin-top: 16px; }
.es-field { max-width: 520px; }
.es-set { color: var(--m-status-success); font-weight: 600; }
.es-suggest { display: flex; gap: 8px; margin-top: 10px; max-width: 640px; }
.es-suggest input { flex: 1; }
.es-actions { margin-top: 16px; }
</style>
